import chroma from 'chroma-js';
import { type TextObjectV2 as MapTextObject, TRANSPARENT } from '../../../../common/types';
import CanvasObject from '../CanvasObject';
import * as geometry from '../common/geometry';
import type SurfaceBase from '../SurfaceBase';
import { getFontSizeToFitForStrings, TEXT_PADDING } from './common';

export default class TextObject extends CanvasObject {
  text: MapTextObject;

  constructor(id: string, surface: SurfaceBase, text: MapTextObject) {
    super(id, surface);
    this.text = text;
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    const [p1, p2, p3, p4] = this.text.geometry.coordinates[0]
      .slice(0, -1)
      .map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat }));

    ctx.beginPath();
    ctx.fillStyle = color;

    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.lineTo(p3.x, p3.y);
    ctx.lineTo(p4.x, p4.y);
    ctx.fill();
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { description, properties } = this.text as MapTextObject;
    const [p1, p2, p3, p4] = this.text.geometry.coordinates[0]
      .slice(0, -1)
      .map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat }));

    const maxWidth = geometry.distance(p1, p2) - 2 * TEXT_PADDING;

    const lines = description.split('\n');
    const fontSize = getFontSizeToFitForStrings(lines, maxWidth, ctx);
    if (fontSize < 12) {
      // too small to draw
      return;
    }

    const height = geometry.distance(p2, p3) - 2 * TEXT_PADDING;
    const center = geometry.polygonCentroid([p1, p2, p3, p4]);
    const angle = geometry.lineAngle(p1, p2);

    // draw background first
    const path = new Path2D();
    path.moveTo(p1.x, p1.y);
    path.lineTo(p2.x, p2.y);
    path.lineTo(p3.x, p3.y);
    path.lineTo(p4.x, p4.y);
    path.closePath();

    if (properties.backgroundColor !== TRANSPARENT) {
      ctx.save();
      ctx.fillStyle = chroma(properties.backgroundColor).alpha(0.75).hex();
      ctx.fill(path);
      ctx.restore();
    }

    if (properties.borderColor && properties.borderColor !== TRANSPARENT) {
      ctx.save();
      ctx.strokeStyle = properties.borderColor;
      ctx.lineWidth = 2;
      ctx.stroke(path);
      ctx.restore();
    }

    ctx.font = `${fontSize}px Inter`;
    ctx.textAlign = 'start';
    ctx.textBaseline = 'top';

    ctx.save();
    ctx.translate(center.x, center.y);
    ctx.rotate(angle);
    ctx.translate(-center.x, -center.y);

    ctx.fillStyle = properties.color;
    ctx.beginPath();
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], center.x - maxWidth / 2, center.y - height / 2 + fontSize * i);
    }

    ctx.restore();
    this.surface.resetCanvasTransform(ctx);
  }
}
